import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Button,
  Select,
  SelectItem,
  Textarea,
} from '@heroui/react';
import React, { useState, useEffect } from 'react';
import {
  OrderBoardCard,
  OrderBoardColumn,
  UpdateCardDetailsRequest,
} from '../../types/orderBoard';

interface CardDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (request: UpdateCardDetailsRequest) => Promise<void>;
  card: OrderBoardCard | null;
  columns: OrderBoardColumn[];
  isLocked: boolean;
}

const CardDetailsModal: React.FC<CardDetailsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  card,
  columns,
  isLocked,
}) => {
  const [selectedColumnId, setSelectedColumnId] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [partNotes, setPartNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when card changes
  useEffect(() => {
    if (card) {
      setSelectedColumnId(card.columnId);
      setNotes(card.notes || '');
      setPartNotes(card.partNotes || '');
    }
  }, [card]);

  const handleConfirm = async () => {
    if (!card) return;

    setIsSubmitting(true);
    try {
      const request: UpdateCardDetailsRequest = {};

      // Only include targetColumnId if it changed
      if (selectedColumnId !== null && selectedColumnId !== card.columnId) {
        request.targetColumnId = selectedColumnId;
      }

      // Always include notes (even if empty, to clear existing notes)
      request.notes = notes.trim() || undefined;
      request.partNotes = partNotes.trim() || undefined;

      await onConfirm(request);
      handleClose();
    } catch {
      // Error handling is done by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (card) {
      setSelectedColumnId(card.columnId);
      setNotes(card.notes || '');
      setPartNotes(card.partNotes || '');
    }
    setIsSubmitting(false);
    onClose();
  };

  if (!card) {
    return null;
  }

  const currentColumn = columns.find(c => c.id === card.columnId);
  const hasChanges =
    (selectedColumnId !== null && selectedColumnId !== card.columnId) ||
    notes.trim() !== (card.notes || '') ||
    partNotes.trim() !== (card.partNotes || '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='md'
      scrollBehavior='inside'
      classNames={{
        base: 'max-h-[90vh]',
        body: 'overflow-y-auto',
      }}
    >
      <ModalContent>
        <ModalHeader>Card Details</ModalHeader>
        <ModalBody>
          <div className='space-y-4'>
            <div>
              <p className='text-sm font-semibold text-gray-900 mb-1'>
                Card Title
              </p>
              <p className='text-sm text-gray-600'>{card.title}</p>
            </div>

            {card.description && (
              <div>
                <p className='text-sm font-semibold text-gray-900 mb-1'>
                  Description
                </p>
                <p className='text-sm text-gray-600'>{card.description}</p>
              </div>
            )}

            <div>
              <p className='text-sm font-semibold text-gray-900 mb-1'>
                Current Status
              </p>
              <p className='text-sm text-gray-600'>
                {currentColumn?.name || 'Unknown'}
              </p>
            </div>

            <Select
              label='Change Status'
              placeholder='Select a column'
              selectedKeys={
                selectedColumnId !== null ? [selectedColumnId.toString()] : []
              }
              onSelectionChange={keys => {
                const selected = Array.from(keys)[0] as string;
                setSelectedColumnId(selected ? parseInt(selected) : null);
              }}
              isDisabled={isLocked}
              description={
                isLocked ? 'Board is locked - cannot change status' : undefined
              }
            >
              {columns.map(column => (
                <SelectItem key={column.id.toString()}>
                  {column.name}
                </SelectItem>
              ))}
            </Select>

            <Textarea
              label='Card Notes'
              placeholder='Record any issues or comments specific to this card...'
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              isDisabled={isLocked}
              description={
                isLocked
                  ? 'Board is locked - cannot edit notes'
                  : 'Notes specific to this card instance'
              }
            />

            {card.productPartId && (
              <Textarea
                label='Part Notes'
                placeholder='Record notes that should apply to all future cards for this part...'
                value={partNotes}
                onChange={e => setPartNotes(e.target.value)}
                rows={3}
                isDisabled={isLocked}
                description={
                  isLocked
                    ? 'Board is locked - cannot edit notes'
                    : 'These notes will persist to future cards created for this part'
                }
              />
            )}

            {isLocked && (
              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
                <p className='text-xs text-yellow-800'>
                  <strong>Board Locked:</strong> This board is locked because
                  the order has been shipped or delivered. No changes can be
                  made.
                </p>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant='ghost'
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={handleConfirm}
            isDisabled={isLocked || !hasChanges || isSubmitting}
            isLoading={isSubmitting}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CardDetailsModal;
